import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import CustomTooltip from "components/Common/CustomTooltip/CustomTooltip";
import { GlobalContext } from "contexts/GlobalContext";
import { PricerContext } from "contexts/PricerContext";
import { DEFAULT_SORT } from "constants/pricerTable";
import {
  getRowClass,
  getRowStyle,
  headerHeight,
  rowHeight,
} from "constants/agGridCommonStyle";
import { allCellsStyle, aggFunc } from "helpers/Pricer/Table";
import { buildQuickAddStagingColumnDefs } from "helpers/QuickAdd/quickAddStagingGridColumns";
import { QUICK_ADD_INPUT_FIELDS } from "helpers/QuickAdd/quickAddInputColumns";
import { isTrader } from "helpers/generalHelper";
import {
  getPricerRowId,
  onPricerCellEditingStarted,
  pricerPrepareDataForCellEditing,
  setPricerModalSort,
} from "helpers/Pricer/utils";
import { updatedPricerData } from "helpers/Pricer/helperAggFunc";
import { updateAdditionalInfoField, updateOnEditCell } from "api/pricerApi";
import "styles/index.css";
import "styles/PricerAuditAgGridLayout.scss";

const noop = () => {};

/**
 * Staged options in the Pricer grid layout. Rows are real (hidden) Pricer
 * options, so this grid edits them exactly like PricerAgGridTable does, and the
 * Pricer cell renderers work through the scoped PricerContext below.
 */
export default function QuickAddStagingGrid({
  rows,
  inputs,
  setRows,
  reloadRows,
}) {
  const gridRef = useRef();
  const gridWrapRef = useRef();
  const [gridHandle, setGridHandle] = useState(null);
  const [rowEditingData, setRowEditingData] = useState(null);
  const [isCellEditing, setIsCellEditing] = useState(false);
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);
  const pricer = useContext(PricerContext);
  const dateSelected = pricer?.state?.dateSelected;

  const notTrader = !isTrader();

  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const scopedPricer = useMemo(
    () => ({
      ...pricer,
      state: {
        ...pricer?.state,
        pricerTablesData: rows,
        pricerRefAgGrid: gridHandle,
        isCellEditing,
        rowEditingData,
      },
      refetchOptionRow: reloadRows,
      setPricerData: setRows,
      setIsCellEditing,
      setRowEditingData,
      setIsOptionRowLoading: noop,
      setIsCellLocking: noop,
      setCellLockingSummaryId: noop,
    }),
    [pricer, rows, gridHandle, isCellEditing, rowEditingData, reloadRows, setRows]
  );

  const fitColumnsToGrid = useCallback(() => {
    gridRef.current?.api?.sizeColumnsToFit();
  }, []);

  const gridContext = useMemo(() => ({ inputs }), [inputs]);

  // Same as PricerAgGridTable: rebuilt when editing toggles, not when
  // rowEditingData changes (a rebuild mid-edit cancels the editor).
  const columnDefs = useMemo(
    () =>
      buildQuickAddStagingColumnDefs({
        darkTheme: dark_Theme,
        notTrader,
        editable: !isCellEditing,
        rowEditingData,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dark_Theme, notTrader, isCellEditing]
  );

  // Same row lock as the Pricer while an edit on that option is saving.
  const rowsWithDisable = useMemo(
    () =>
      rows?.map((data) => ({
        ...data,
        isDisable: isCellEditing && rowEditingData?.summary_id === data.summary_id,
      })),
    [rows, isCellEditing, rowEditingData]
  );

  const getRowStyleStable = useCallback(
    (params) => getRowStyle(params, rowsWithDisable),
    [rowsWithDisable]
  );

  const onGridReady = useCallback((params) => {
    setGridHandle({ api: params.api, columnApi: params.columnApi });
    params.api.expandAll();
    setPricerModalSort(params, DEFAULT_SORT);
    requestAnimationFrame(() => params.api.sizeColumnsToFit());
  }, []);

  const onCellEditingStarted = useCallback(
    (event) => onPricerCellEditingStarted(event, setRowEditingData, dark_Theme),
    [dark_Theme]
  );

  // Same flow as PricerAgGridTable.onCellEditingStopped.
  const onCellEditingStopped = useCallback(
    (event) => {
      setIsCellEditing(false);

      const oldNum = Number(event.oldValue);
      const newNum = Number(event.newValue);
      if (!Number.isNaN(oldNum) && !Number.isNaN(newNum) && oldNum === newNum) {
        setRowEditingData(null);
        return;
      }
      if (String(event.oldValue ?? "") === String(event.newValue ?? "")) {
        setRowEditingData(null);
        return;
      }

      const node = event.node;
      const rowData = node.group ? node.aggData : node.data;
      const field = event.colDef?.field;

      setIsCellEditing(true);

      if (QUICK_ADD_INPUT_FIELDS.includes(field)) {
        updateAdditionalInfoField(rowData?.summary_id, field, event.newValue)
          .catch(noop)
          .finally(() => {
            setIsCellEditing(false);
            setRowEditingData(null);
            reloadRows();
          });
        return;
      }

      updateOnEditCell(
        pricerPrepareDataForCellEditing(rowData, event, dateSelected)
      )
        .then((res) => {
          setRows(updatedPricerData(res.data.data, rowsRef.current));
          gridRef.current?.api?.refreshCells({ force: true });
        })
        .catch(() => reloadRows())
        .finally(() => {
          setIsCellEditing(false);
          setRowEditingData(null);
        });
    },
    [dateSelected, reloadRows, setRows]
  );

  useEffect(() => {
    if (!gridRef.current?.api) return;
    gridRef.current.api.expandAll();
    gridRef.current.api.refreshCells({ force: true });
    fitColumnsToGrid();
  }, [rows, inputs, columnDefs, fitColumnsToGrid]);

  useEffect(() => {
    const wrap = gridWrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => fitColumnsToGrid());
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [fitColumnsToGrid, rows]);

  if (!rows?.length) return null;

  return (
    <PricerContext.Provider value={scopedPricer}>
      <div className="quick-add-staging-grid">
        <div
          ref={gridWrapRef}
          className={`pricer-table ${
            dark_Theme
              ? "pricer-ag-grid__container_dark"
              : "pricer-ag-grid__container"
          } quick-add-staging-grid__wrap`}
        >
          <AgGridReact
            ref={gridRef}
            suppressMovableColumns
            suppressContextMenu
            domLayout="autoHeight"
            getRowId={getPricerRowId}
            getRowStyle={getRowStyleStable}
            getRowClass={getRowClass}
            className={`ag-theme-alpine pricer-ag-grid__table quick-add-staging-grid__ag${
              dark_Theme ? " ag-theme-balham-dark" : " ag-theme-balham"
            }${notTrader ? "" : " pricer-ag-grid__table_trader"}`}
            components={{ customTooltip: CustomTooltip }}
            getRowHeight={rowHeight}
            defaultColDef={allCellsStyle}
            headerHeight={headerHeight}
            columnDefs={columnDefs}
            rowData={rowsWithDisable}
            context={gridContext}
            onGridReady={onGridReady}
            onFirstDataRendered={fitColumnsToGrid}
            onCellEditingStarted={onCellEditingStarted}
            onCellEditingStopped={onCellEditingStopped}
            onCellValueChanged={(event) => {
              if (event.colDef.field === "tenor") onCellEditingStopped(event);
            }}
            tooltipShowDelay={0}
            getGroupRowAgg={(params) => aggFunc(params)}
            groupDisplayType="custom"
            suppressAggFuncInHeader
            animateRows={false}
            suppressRowHoverHighlight
            suppressRowClickSelection
            groupDefaultExpanded={-1}
            enableGroupEdit
            stopEditingWhenCellsLoseFocus
          />
        </div>
      </div>
    </PricerContext.Provider>
  );
}
